import type { FC } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { translate } from "app/filters/i18nFilter";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";

import type { WebsitePageResults } from "./types";
import {
    trackAutocompleteOpen,
    trackAutocompleteSelectSite,
    trackCompetitorsCleared,
    updateUrlWithCompetitors,
} from "./utils";

const MAX_ALLOWED_COMPETITORS = 2;

interface IQueryBarCompareButton {
    similarSites: ISimilarInfoItem[];
    sitesToExclude: IChosenItem[];
    onAddCompetitor: (item: IWebsitePageResult) => void;
    onClearCompetitors: () => void;
    maxAllowedCompetitors?: number;
    currentCompetitors: WebsitePageResults;
}

export const QueryBarCompareButton: FC<IQueryBarCompareButton> = ({
    similarSites,
    sitesToExclude,
    onAddCompetitor,
    onClearCompetitors,
    maxAllowedCompetitors = MAX_ALLOWED_COMPETITORS,
    currentCompetitors = [],
}) => {
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const isMaxCompetitorsSelected = currentCompetitors.length >= maxAllowedCompetitors;
    const buttonText = translate(isMaxCompetitorsSelected ? "common.clear_all" : "global.compare");

    const handleOnClick = () => {
        if (isMaxCompetitorsSelected) {
            onClearCompetitors();
            updateUrlWithCompetitors([]);
            trackCompetitorsCleared();
        } else {
            setIsAutocompleteOpen(true);
        }
    };

    const handleAddNewCompetitor = (competitor: IWebsitePageResult) => {
        trackAutocompleteSelectSite(competitor.name);
        setIsAutocompleteOpen(false);
        onAddCompetitor(competitor);
    };

    return (
        <Box
            position="relative"
            data-automation="inline-website-query-bar-compare-button"
        >
            {!isAutocompleteOpen ? (
                <Box marginX="8px 0">
                    <IconButton
                        iconName={"add"}
                        iconSize={isMaxCompetitorsSelected ? "0px" : "xs"}
                        type={isMaxCompetitorsSelected ? "flat" : "outlined"}
                        onClick={handleOnClick}
                    >
                        <Text
                            fontVariant="button"
                            color={colorsPalettes.blue[500]}
                        >
                            {buttonText}
                        </Text>
                    </IconButton>
                </Box>
            ) : (
                <ClickOutsideWrapper onClickOutside={() => setIsAutocompleteOpen(false)}>
                    <AutocompleteWebsitePagesCompareItem
                        className="CompareButtonAutocompleteInlineWebsiteQueryBar"
                        similarSites={similarSites}
                        showExtendedTypeResults={{
                            exact: false,
                            folder: false,
                        }}
                        autocompleteProps={{
                            placeholder: translate("querybar.websitePages.comparebutton.autocomplete.placeholder"),
                            top: -20,
                            width: 400,
                            onFocus: () => {
                                trackAutocompleteOpen();
                            },
                        }}
                        onClick={handleAddNewCompetitor}
                        onCloseButtonClick={() => setIsAutocompleteOpen(false)}
                        excludes={sitesToExclude}
                        overrideEnterFunc={(_, item) => {
                            if (item) {
                                handleAddNewCompetitor(item as IWebsitePageResult);
                            }
                            setIsAutocompleteOpen(false);
                        }}
                    />
                </ClickOutsideWrapper>
            )}
        </Box>
    );
};
